import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { MenuItem, SelectItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { TokenService } from 'projects/admin/src/app/services/token.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { MIN_PASSWORD_LENGTH } from 'projects/admin/src/app/shared/constants';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { LANGUAGES, SALUTATIONS } from 'projects/core/src/lib/constants';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';

@Component({
  selector: 'app-profile-basedata',
  templateUrl: './profile-basedata.component.html',
  styleUrls: ['./profile-basedata.component.scss']
})
export class ProfileBasedataComponent extends BaseForm implements OnInit, OnChanges {

  @Input() user: UserModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public loading = false;
  public country: string | null = null;
  public timezone: string | null = null;
  public salutations: Array<SelectItem> = [];
  public languages: Array<SelectItem> = [];
  public items: Array<MenuItem> = [{
    label: this.transloco.translate('my_profile.refresh_token'),
    icon: 'pi pi-sync',
    command: () => {
      this.onRefreshToken();
    }
  }];
  public roleList: string = null;

  public form: UntypedFormGroup = this.fb.group({
    gender: new UntypedFormControl('', [Validators.required]),
    firstname: new UntypedFormControl('', [Validators.required]),
    lastname: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required, CustomValidators.email()]),
    phone: new UntypedFormControl(''),
    language: new UntypedFormControl('', [Validators.required]),
    mobile: new UntypedFormControl(''),
    company: new UntypedFormControl(''),
    passwords: this.fb.group({
      old: new UntypedFormControl(''),
      new: new UntypedFormControl(''),
      confirm: new UntypedFormControl(''),
    }, {
      validators: [this.passwordValidator]
    }),
    tz: this.fb.group({}),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private transloco: TranslocoService,
    private auth: AuthStateService,
    private token: TokenService,
    private helper: HelperService
  ) {
    super();
  }

  ngOnInit(): void {
    this.salutations = FormHelper.getSelectList(SALUTATIONS, null, this.transloco);
    this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
  }

  ngOnChanges(): void {
    this.country = this.user?.country;
    this.timezone = this.user?.timezone;
    this.roleList = this.user?.rolenames.map((roleName: string) => {
      return this.transloco.translate(roleName);
    }).join(', ');
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload = {
        gender: this.form.value.gender,
        firstname: this.form.value.firstname,
        lastname: this.form.value.lastname,
        email: this.form.value.email,
        phone: this.form.value.phone,
        mobile: this.form.value.mobile,
        language: this.form.value.language,
        company: this.form.value.company,
        country: this.country,
        timezone: this.timezone,
        oldpassword: this.form.value.passwords.old,
        newpassword: this.form.value.passwords.new,
      };
      if (this.form.value.language === 'DE') {
        this.helper.setLanguage('de');
        this.ui.setPageTitle(this.transloco.translate('my_profile.my_profile'));
      } else {
        this.helper.setLanguage('en');
        this.ui.setPageTitle(this.transloco.translate('my_profile.my_profile'));
      }
      this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
      this.api.saveMyProfile(payload).subscribe({
        next: (result: any) => {
          this.user.deserialize(result.data);
          this.api.refreshToken(this.token.getRefreshToken()).subscribe({
            next: (tokenResult: any) => {
              this.auth.refreshToken(tokenResult.access_token, tokenResult.refresh_token);
            }
          }).add(() => {
            this.ui.toastSuccess(this.transloco.translate('my_profile.profile_saved'), this.transloco.translate('common.saved'));
            this.stopEdit();
            this.submitting = false;
          });
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
          this.submitting = false;
        }
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public onRefreshToken(): void {
    this.api.refreshToken(this.token.getRefreshToken()).subscribe({
      next: (tokenResult: any) => {
        this.auth.refreshToken(tokenResult.access_token, tokenResult.refresh_token);
      }
    }).add(() => {
      if (this.auth.hasNoRights()) {
        this.router.navigate(['/dashboard']);
      } else {
        this.ui.toastSuccess(this.transloco.translate('my_profile.token_refreshed'), this.transloco.translate('common.success'));
      }
    });
  }

  protected startEdit(): void {
    if (this.user !== null) {
      this.form.patchValue({
        gender: this.user.gender,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        email: this.user.email,
        phone: this.user.phone,
        mobile: this.user.mobile,
        language: this.user.language,
        company: this.user.company,
      });
    }
    this.setEdit.emit('basedata');
    super.startEdit();
    this.ui.setFormFocus();
  }

  protected stopEdit(): void {
    this.ui.hideMessage();
    this.form.reset();
    this.country = this.user.country;
    this.timezone = this.user.timezone;
    this.inEdit = false;
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus('.autofocus');
  }

  private passwordValidator(group: UntypedFormGroup): void {
    const old = group.get('old')?.value;
    const password = group.get('new')?.value;
    const password2 = group.get('confirm')?.value;
    if (!old && (password || password2)) {
      group.get('old').setErrors({ required: true });
    } else {
      group.get('old').setErrors(null);
      group.get('new').setErrors(null);
      group.get('confirm').setErrors(null);
      if (old) {
        if (!password) {
          group.get('new')?.setErrors({ required: true });
        } else {
          const errors = [];
          if (password.length < MIN_PASSWORD_LENGTH) {
            errors.push('passwordLength');
          }
          if (!(/\d/.test(password) && /[A-Z]/.test(password) && /[a-z]/.test(password))) {
            errors.push('passwordStrength');
          }
          if (errors.length > 0) {
            const errorList = {};
            errors.forEach((error: string) => {
              errorList[error] = true;
            });
            group.get('new')?.setErrors(errorList);
          } else {
            group.get('new')?.setErrors(null);
          }
          if (!password2) {
            group.get('confirm')?.setErrors({ required: true });
          } else if (password !== password2) {
            group.get('confirm')?.setErrors({ passwordMatch: true });
          }
        }
      }
    }
  }

}
